import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import Navbar from "../components/Navbar"; // Import Navbar
import StickyFooter from "../components/StickyFooter";
import "../css/absensi.css"; // Import CSS

const Absensi = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceEmbedding, setFaceEmbedding] = useState(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [absensiType, setAbsensiType] = useState("in"); // State untuk tipe absensi
  const token = localStorage.getItem("token");
  const { id } = jwtDecode(token);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [status, setStatus] = useState("in");
  const [userAction, setUserAction] = useState(null); // Menyimpan tindakan yang harus dilakukan pengguna
  const [actionCompleted, setActionCompleted] = useState(false); // Menyimpan status apakah tindakan sudah selesai
  const [blinkVerified, setBlinkVerified] = useState(false); // State untuk menyimpan status verifikasi kedipan mata
  const [isBlinkButtonDisabled, setIsBlinkButtonDisabled] = useState(false); // State untuk menonaktifkan tombol kedipan mata
  const [isSmileButtonDisabled, setIsSmileButtonDisabled] = useState(false); // State untuk menonaktifkan tombol senyum

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
    }
  }, []);

  const extractEmbedding = useCallback((landmarks) => {
    // Ekstrak embedding dari landmarks wajah (x, y, z)
    return landmarks.map((point) => [point.x, point.y, point.z]);
  }, []);

  const detectBlink = useCallback((landmarks) => {
    // Indeks landmark mata berdasarkan MediaPipe
    const leftEye = [33, 159, 158, 133]; // Mata kiri (atas, bawah, samping)
    const rightEye = [263, 386, 387, 362]; // Mata kanan

    // Fungsi menghitung EAR
    const computeEAR = (eye) => {
      const vertical1 = Math.hypot(
        landmarks[eye[1]].x - landmarks[eye[2]].x,
        landmarks[eye[1]].y - landmarks[eye[2]].y
      );
      const horizontal = Math.hypot(
        landmarks[eye[0]].x - landmarks[eye[3]].x,
        landmarks[eye[0]].y - landmarks[eye[3]].y
      );
      return vertical1 / horizontal;
    };

    const leftEAR = computeEAR(leftEye);
    const rightEAR = computeEAR(rightEye);

    // Rata-rata EAR
    const ear = (leftEAR + rightEAR) / 2.0;
    return ear < 0.19; // Jika EAR < 0.2, dianggap berkedip
  }, []);

  const detectSmile = useCallback((landmarks) => {
    // Landmark untuk mulut (indeks sesuai dengan FaceMesh)
    const mouthLeft = landmarks[61]; // Contoh landmark mulut kiri
    const mouthRight = landmarks[291]; // Contoh landmark mulut kanan

    // Hitung jarak antara titik-titik tertentu di mulut
    const mouthDistance = Math.sqrt(
      Math.pow(mouthLeft.x - mouthRight.x, 2) +
        Math.pow(mouthLeft.y - mouthRight.y, 2)
    );

    // Jika jarak lebih dari threshold, mulut dianggap terbuka (tersenyum)
    const smileThreshold = 0.1; // Threshold untuk mendeteksi senyum

    return mouthDistance > smileThreshold;
  }, []);

  const drawFaceMesh = useCallback((landmarks) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#00FF00";
    ctx.lineWidth = 2;

    landmarks.forEach((point) => {
      ctx.beginPath();
      ctx.arc(
        point.x * canvas.width,
        point.y * canvas.height,
        2,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = "red";
      ctx.fill();
      ctx.closePath();
    });
  }, []);

  const captureImageAsBase64 = useCallback(() => {
    return new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      if (canvas && ctx) {
        // Menyalin gambar wajah yang terdeteksi ke canvas
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        // Mengambil gambar sebagai base64
        const imageBase64 = canvas.toDataURL("image/jpeg");
        resolve(imageBase64);
      } else {
        reject("Gagal menangkap gambar.");
      }
    });
  }, []);

  const handleUpload = useCallback(async () => {
    if (!latitude || !longitude) {
      setMessage("Mohon pastikan GPS sudah tersedia.");
      return;
    }
    if (!faceEmbedding || !id) {
      setMessage("Gagal mengirim: Data tidak lengkap.");
      return;
    }
    if (!actionCompleted) {
      setMessage("Silakan selesaikan tindakan yang diminta.");
      return;
    }

    try {
      setSending(true);
      setMessage("");

      const base64Image = await captureImageAsBase64();

      const data = {
        user_id: id,
        face_descriptor: faceEmbedding,
        type: status,
        latitude: latitude,
        longitude: longitude,
        photo: base64Image,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/attendance/record`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setMessage("✅ Absensi berhasil dikirim!");
      } else {
        setMessage("❌ Gagal mengirim data.");
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage("⚠ Terjadi kesalahan saat mengirim data.");
    }

    setSending(false);
  }, [
    latitude,
    longitude,
    faceEmbedding,
    id,
    status,
    actionCompleted,
    captureImageAsBase64,
  ]);

  const handleActionChange = useCallback(
    (action) => {
      setUserAction(action);
      if (!blinkVerified) {
        setBlinkVerified(false); // Reset status verifikasi
      }
    },
    [blinkVerified]
  );

  const isUploadDisabled = useMemo(() => {
    return (
      !latitude ||
      !longitude ||
      !blinkVerified ||
      !isSmileButtonDisabled ||
      sending
    );
  }, [latitude, longitude, blinkVerified, isSmileButtonDisabled, sending]);

  // useEffect(() => {
  //   if (!token) {
  //     return navigate("/login");
  //   }

  //   // startCamera();
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       setLatitude(position.coords.latitude);
  //       setLongitude(position.coords.longitude);
  //     },
  //     (error) => {
  //       console.error("Error getting location:", error);
  //       setMessage("Tidak bisa mengakses lokasi, periksa izin GPS.");
  //     }
  //   );

  //   if (!videoRef.current) return;

  //   const faceMesh = new FaceMesh({
  //     locateFile: (file) =>
  //       `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
  //   });
  //   console.log("Current FaceMesh Instance:", faceMesh);

  //   faceMesh.setOptions({
  //     maxNumFaces: 1,
  //     refineLandmarks: true,
  //     minDetectionConfidence: 0.5,
  //     minTrackingConfidence: 0.5,
  //   });

  //   faceMesh.onResults((results) => {
  //     if (results.multiFaceLandmarks.length > 0) {
  //       setFaceDetected(true);
  //       const landmarks = results.multiFaceLandmarks[0];

  //       // Deteksi interaksi pengguna
  //       if (userAction === "blink" && detectBlink(landmarks)) {
  //         console.log("kedipan terdeteksi");
  //         setBlinkVerified(true); // Tandai kedipan mata terverifikasi
  //         setIsBlinkButtonDisabled(true); // Nonaktifkan tombol kedipan mata
  //       } else if (userAction === "smile" && detectSmile(landmarks)) {
  //         console.log("senyum terdeteksi");
  //         setIsSmileButtonDisabled(true); // Nonaktifkan tombol senyum
  //       }

  //       setFaceEmbedding(extractEmbedding(landmarks)); // Ekstrak embedding wajah
  //       drawFaceMesh(landmarks);
  //     } else {
  //       setFaceDetected(false);
  //       setFaceEmbedding(null);
  //     }
  //   });

  //   setTimeout(() => {
  //     if (videoRef.current) {
  //       const camera = new Camera(videoRef.current, {
  //         onFrame: async () => {
  //           await faceMesh.send({ image: videoRef.current });
  //         },
  //         width: 640,
  //         height: 480,
  //       });

  //       camera.start();
  //       setModelsLoaded(true);
  //       return () => {
  //         camera.stop(); // Hentikan kamera saat komponen unmount
  //         faceMesh.close(); // Tutup faceMesh
  //         if (videoRef.current && videoRef.current.srcObject) {
  //           const stream = videoRef.current.srcObject;
  //           const tracks = stream.getTracks();
  //           tracks.forEach((track) => track.stop());
  //         }
  //       };
  //     }
  //   }, 500);
  // }, [
  //   navigate,
  //   token,
  //   userAction,
  //   startCamera,
  //   detectBlink,
  //   detectSmile,
  //   drawFaceMesh,
  //   extractEmbedding,
  //   actionCompleted,
  // ]);

  useEffect(() => {
    if (!token) {
      return navigate("/login");
    }

    let isMounted = true; // Cegah pemanggilan setState setelah unmount
    const abortController = new AbortController(); // Untuk membatalkan async task

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (isMounted) {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        if (isMounted)
          setMessage("Tidak bisa mengakses lokasi, periksa izin GPS.");
      }
    );

    if (!videoRef.current) return;

    const faceMesh = new FaceMesh({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });
    faceMeshRef.current = faceMesh;

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    faceMesh.onResults((results) => {
      if (!isMounted) return; // Pastikan hanya berjalan jika masih di halaman yang sama

      if (results.multiFaceLandmarks.length > 0) {
        setFaceDetected(true);
        const landmarks = results.multiFaceLandmarks[0];

        if (userAction === "blink" && detectBlink(landmarks)) {
          console.log("kedipan terdeteksi");
          setBlinkVerified(true);
          setIsBlinkButtonDisabled(true);
        } else if (userAction === "smile" && detectSmile(landmarks)) {
          console.log("senyum terdeteksi");
          setIsSmileButtonDisabled(true);
        }

        setFaceEmbedding(extractEmbedding(landmarks));
        drawFaceMesh(landmarks);
      } else {
        setFaceDetected(false);
        setFaceEmbedding(null);
      }
    });

    const camera = new Camera(videoRef.current, {
      onFrame: async () => {
        if (!isMounted || !videoRef.current) return; // Hindari akses elemen yang sudah di-unmount

        try {
          await faceMesh.send({ image: videoRef.current });
        } catch (error) {
          if (!isMounted || abortController.signal.aborted) return;
          console.error("FaceMesh error:", error);
        }
      },
      width: 640,
      height: 480,
    });
    cameraRef.current = camera;

    camera.start();
    setModelsLoaded(true);

    // Cleanup function untuk menghentikan FaceMesh dan Kamera saat komponen unmount
    return () => {
      isMounted = false;
      abortController.abort(); // Batalkan semua async operation

      if (cameraRef.current) {
        cameraRef.current.stop();
        cameraRef.current = null;
      }
      if (faceMeshRef.current) {
        faceMeshRef.current.close();
        faceMeshRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, [
    navigate,
    token,
    userAction,
    detectBlink,
    detectSmile,
    drawFaceMesh,
    extractEmbedding,
  ]);

  useEffect(() => {
    if (latitude && longitude && blinkVerified && isSmileButtonDisabled) {
      setActionCompleted(true);
    } else {
      setActionCompleted(false);
    }
  }, [latitude, longitude, blinkVerified, isSmileButtonDisabled]);

  return (
    <div className="navbarContainer">
      <Navbar title="Absensi" />
      <div className="absensi-container">
        <h2>Absensi Online</h2>
        {!modelsLoaded && (
          <div className="loading-container">
            <p>⏳ Memuat model deteksi wajah...</p>
          </div>
        )}

        <div className="video-container">
          <video ref={videoRef} autoPlay playsInline></video>
          <canvas ref={canvasRef}></canvas>
        </div>
        {faceDetected ? (
          <p className="face-detected">✔ Wajah terdeteksi!</p>
        ) : (
          <p className="face-not-detected">❌ Wajah tidak terdeteksi.</p>
        )}
        <div className="interaction-container">
          <p>Lakukan tindakan berikut:</p>
          {latitude && longitude && <p>✅ Lokasi terverifikasi</p>}
          {blinkVerified ? (
            <p>✅ Kedipan mata terverifikasi!</p>
          ) : (
            <button
              onClick={() => handleActionChange("blink")}
              disabled={isBlinkButtonDisabled || blinkVerified} // Nonaktifkan jika kedipan sudah terdeteksi
              className={`blink-button ${
                isBlinkButtonDisabled ? "disabled" : ""
              }`}
            >
              Kedipkan Mata
            </button>
          )}
          {isSmileButtonDisabled ? (
            <p>✅ Senyum terdeteksi!</p>
          ) : (
            <button
              onClick={() => handleActionChange("smile")}
              disabled={isSmileButtonDisabled}
              className={`smile-button ${
                isSmileButtonDisabled ? "disabled" : ""
              }`}
            >
              Tersenyum
            </button>
          )}

          {/* Dropdown untuk memilih tipe absensi */}
          <div className="absensi-dropdown">
            <label htmlFor="absensiType">Tipe Absensi: </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="status-dropdown"
            >
              <option value="in">Datang</option>
              <option value="break_out">Istirahat</option>
              <option value="break_in">Kembali</option>
              <option value="out">Pulang</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleUpload}
          disabled={isUploadDisabled}
          className={`kirim-button ${isUploadDisabled ? "disabled" : ""}`}
        >
          {sending ? "Mengirim..." : "Kirim"}
        </button>

        {message && <p className="message">{message}</p>}
      </div>
      <StickyFooter />
    </div>
  );
};

export default Absensi;
